import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, MapPin, Users, X, Trash2, Navigation } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import CustomAlertModal from '../modals/CustomAlertModal';
import { auth } from '../../config/firebaseConfig';
import { API_BASE_URL } from '../../config/apiConfig';
import './styles/MobileScheduleView.css'; // 스타일 재사용

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const PARTICIPANT_LABELS = 'ABCDEFGHIJKLMNOP';
const PARTICIPANT_COLORS = ['#EF4444', '#8B5CF6', '#10B981', '#F59E0B', '#EC4899', '#06B6D4', '#F97316', '#6366F1'];

// Google 인코딩 폴리라인 → [[lat,lng], ...] 디코더
function decodePolyline(encoded) {
   let idx = 0, lat = 0, lng = 0, result = [];
   while (idx < encoded.length) {
      let b, shift = 0, r = 0;
      do { b = encoded.charCodeAt(idx++) - 63; r |= (b & 31) << shift; shift += 5; } while (b >= 32);
      lat += r & 1 ? ~(r >> 1) : r >> 1;
      shift = 0; r = 0;
      do { b = encoded.charCodeAt(idx++) - 63; r |= (b & 31) << shift; shift += 5; } while (b >= 32);
      lng += r & 1 ? ~(r >> 1) : r >> 1;
      result.push([lat / 1e5, lng / 1e5]);
   }
   return result;
}

/**
 * MapModal - 지도 모달 (장소 보기 + 길찾기)
 * participantLocations: [{ name, lat, lng, isSelf, locationSource }]
 * - 장소 보기 탭: 각 참여자 → 목적지 경로선 + 클릭 포커스
 * - 길찾기 탭: 내 현재위치 → 목적지 iframe
 */
export const MapModal = ({ address, lat, lng, onClose, participantLocations = [] }) => {
   const [mode, setMode] = useState('map');
   const [currentPos, setCurrentPos] = useState(null);
   const [geoError, setGeoError] = useState(null);
   const [geoLoading, setGeoLoading] = useState(false);
   const [jsReady, setJsReady] = useState(() => !!(window.google?.maps));
   const [jsLoadFailed, setJsLoadFailed] = useState(false); // Maps JS 로드 실패 여부
   const [focusedIdx, setFocusedIdx] = useState(null);
   const [routeDurations, setRouteDurations] = useState({});   // { idx: '15분' }
   const [routeLoading, setRouteLoading] = useState({});       // { idx: true }
   const [routePaths, setRoutePaths] = useState({});           // { idx: [[lat,lng],...] }

   const participantMapRef = useRef(null);
   const mapInitRef = useRef(false);
   const mapInstanceRef = useRef(null);
   const polylinesRef = useRef([]);
   const leafletDivRef = useRef(null);
   const leafletMapRef = useRef(null);
   const leafletPolylinesRef = useRef({});  // 이미 그린 경로선 (중복 방지)
   const pendingPathsRef = useRef({});      // 지도 준비 전에 도착한 경로 데이터 버퍼
   const boundsRef = useRef(null);
   const httpFallbackTriggered = useRef(false); // DirectionsService 실패로 HTTP 폴백 시작됐는지
   const membersRef = useRef([]);

   const membersWithCoords = participantLocations.filter(p => p.lat && p.lng);
   // ★ membersRef는 전체 배열(participantLocations) 기준 — focusedIdx와 인덱스 일치
   membersRef.current = participantLocations;
   const useJsMap = lat && lng && GOOGLE_MAPS_API_KEY;

   // Maps JS API 로드
   useEffect(() => {
      if (!useJsMap) return;

      // 도메인 제한 등 인증 실패 시 Google Maps JS가 gm_authFailure 콜백 호출 → 즉시 iframe 폴백
      const prevAuthFailure = window.gm_authFailure;
      window.gm_authFailure = () => {
         setJsLoadFailed(true);
         if (typeof prevAuthFailure === 'function') prevAuthFailure();
      };

      if (window.google?.maps) { setJsReady(true); return; }
      if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
         let elapsed = 0;
         const check = setInterval(() => {
            if (window.google?.maps) { setJsReady(true); clearInterval(check); return; }
            elapsed += 100;
            if (elapsed >= 5000) { clearInterval(check); setJsLoadFailed(true); }
         }, 100);
         return () => { clearInterval(check); window.gm_authFailure = prevAuthFailure; };
      }
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places&language=ko&region=KR`;
      script.async = true;
      script.onload = () => setJsReady(true);
      script.onerror = () => setJsLoadFailed(true);
      document.head.appendChild(script);
      return () => { window.gm_authFailure = prevAuthFailure; };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [useJsMap]);

   // Maps JS 12초 후에도 로드 안 되면 강제 fallback
   useEffect(() => {
      if (!useJsMap || jsReady) return;
      const timer = setTimeout(() => setJsLoadFailed(true), 12000);
      return () => clearTimeout(timer);
   }, [useJsMap, jsReady]);

   // Maps JS 없을 때: 서버 HTTP 프록시로 모든 참여자 경로 계산
   useEffect(() => {
      if (!jsLoadFailed || mode !== 'map') return;
      // DirectionsService 폴백이 이미 개별 멤버별 HTTP 계산 처리 중이면 중복 실행 방지
      if (httpFallbackTriggered.current) return;
      httpFallbackTriggered.current = true; // 이 effect가 먼저 시작했음을 표시 → httpFallbackSingle 중복 방지
      const members = participantLocations.filter(p => p.lat && p.lng);
      if (members.length === 0) return;

      // "계산 중..." 표시 (불참 멤버 제외)
      const loading = {};
      participantLocations.forEach((p, i) => { if (p.lat && p.lng && !p.isRejected) loading[i] = true; });
      setRouteLoading(loading);

      const destStr = lat && lng ? `${lat},${lng}` : address;

      const calcAll = async () => {
         let token = null;
         try { token = await auth.currentUser?.getIdToken(); } catch (_) {}
         const headers = token ? { Authorization: `Bearer ${token}` } : {};

         for (let i = 0; i < participantLocations.length; i++) {
            const p = participantLocations[i];
            if (!p.lat || !p.lng) continue;
            if (p.isRejected) continue; // [5단계] 불참 멤버 경로 스킵
            const originStr = `${parseFloat(p.lat)},${parseFloat(p.lng)}`;
            let found = false;
            for (const tmode of ['transit', 'driving', 'walking']) {
               try {
                  const res = await fetch(
                     `${API_BASE_URL}/api/engine/directions?origin=${encodeURIComponent(originStr)}&dest=${encodeURIComponent(destStr)}&mode=${tmode}`,
                     { headers }
                  );
                  const data = await res.json();
                  if (data.status === 'OK' && data.routes?.length > 0) {
                     const leg = data.routes[0].legs[0];
                     setRouteDurations(prev => ({ ...prev, [i]: leg.duration?.text }));
                     const encoded = data.routes[0].overview_polyline?.points;
                     if (encoded) setRoutePaths(prev => ({ ...prev, [i]: decodePolyline(encoded) }));
                     found = true;
                     break;
                  }
               } catch (_) {}
            }
            setRouteLoading(prev => ({ ...prev, [i]: false }));
         }
      };
      calcAll();
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [jsLoadFailed, mode]);

   // Leaflet 지도: jsLoadFailed 시 조원 마커 + 경로선 표시 (OpenStreetMap, API키 불필요)
   useEffect(() => {
      if (!jsLoadFailed || mode !== 'map' || !lat || !lng) return;
      const destLat = parseFloat(lat);
      const destLng = parseFloat(lng);

      const init = async () => {
         // 1) CSS 로드 + 적용 완료까지 대기 (onload 없으면 타일 위치 계산 오류)
         // ※ rel="preload" 링크와 구분하기 위해 rel="stylesheet" 조건 필수
         await new Promise(resolve => {
            if (document.querySelector('link[rel="stylesheet"][href*="leaflet.min.css"]')) { resolve(); return; }
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
            link.onload = resolve;
            link.onerror = resolve;
            document.head.appendChild(link);
         });

         // 2) JS 로드 완료까지 대기
         if (!window.L) {
            await new Promise((resolve, reject) => {
               const s = document.createElement('script');
               s.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
               s.onload = resolve; s.onerror = reject;
               document.head.appendChild(s);
            });
         }

         if (leafletMapRef.current) return;

         // 3) 브라우저가 컨테이너 레이아웃 확정할 때까지 대기
         await new Promise(resolve => setTimeout(resolve, 50));
         if (leafletMapRef.current) return;

         const el = leafletDivRef.current;
         if (!el) return;

         const L = window.L;
         const map = L.map(el, { zoomControl: true, preferCanvas: true })
                      .setView([destLat, destLng], 13);
         leafletMapRef.current = map;

         L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap', maxZoom: 19
         }).addTo(map);

         // 목적지 마커
         L.marker([destLat, destLng], { icon: L.divIcon({
            html: `<div style="background:#EF4444;color:#fff;width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:15px;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4)">📍</div>`,
            className: '', iconSize: [30,30], iconAnchor: [15,15]
         })}).addTo(map).bindPopup(address);

         // 조원 마커
         const bounds = L.latLngBounds([[destLat, destLng]]);
         participantLocations.forEach((p, i) => {
            if (!p.lat || !p.lng) return;
            // [5단계] 불참 멤버: 회색 ✕ 마커
            const color = p.isRejected ? '#9CA3AF' : PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length];
            const label = p.isRejected ? '✕' : (PARTICIPANT_LABELS[i] || String(i+1));
            const pLat = parseFloat(p.lat), pLng = parseFloat(p.lng);
            L.marker([pLat, pLng], { icon: L.divIcon({
               html: `<div style="background:${color};color:#fff;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.4)">${label}</div>`,
               className: '', iconSize: [26,26], iconAnchor: [13,13]
            })}).addTo(map).bindPopup(`${p.name}${p.isRejected ? ' (불참)' : p.isSelf ? ' (나)' : ''}`);
            bounds.extend([pLat, pLng]);
         });

         if (participantLocations.some(p => p.lat && p.lng)) {
            map.fitBounds(bounds, { padding: [30,30] });
         }

         // 버퍼에 쌓인 경로선 즉시 그리기
         Object.entries(pendingPathsRef.current).forEach(([idx, path]) => {
            if (!path || path.length === 0 || leafletPolylinesRef.current[idx]) return;
            const i = parseInt(idx);
            const color = PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length];
            leafletPolylinesRef.current[idx] = L.polyline(path, { color, weight: 5, opacity: 0.85 }).addTo(map);
         });
      };

      init().catch(() => {});
      return () => {
         if (leafletMapRef.current) { leafletMapRef.current.remove(); leafletMapRef.current = null; }
         leafletPolylinesRef.current = {};
         pendingPathsRef.current = {};
      };
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [jsLoadFailed, mode]);

   // routePaths 업데이트 시 Leaflet 경로선 그리기
   // - 지도 준비 전 도착하면 pendingPathsRef에 버퍼링 → 지도 준비 후 그림
   useEffect(() => {
      if (Object.keys(routePaths).length === 0) return;

      // 새로 추가된 경로만 처리
      Object.entries(routePaths).forEach(([idx, path]) => {
         if (!path || path.length === 0) return;
         if (leafletPolylinesRef.current[idx]) return; // 이미 그린 경로 스킵
         pendingPathsRef.current[idx] = path; // 버퍼에 저장
      });

      const drawPending = () => {
         const map = leafletMapRef.current;
         if (!map || !window.L) return false;
         Object.entries(pendingPathsRef.current).forEach(([idx, path]) => {
            if (leafletPolylinesRef.current[idx]) return;
            const i = parseInt(idx);
            const color = PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length];
            const poly = window.L.polyline(path, { color, weight: 5, opacity: 0.85 }).addTo(map);
            leafletPolylinesRef.current[idx] = poly;
         });
         return true;
      };

      if (!drawPending()) {
         // 지도 아직 준비 안 됨 → 200ms마다 재시도
         const timer = setInterval(() => { if (drawPending()) clearInterval(timer); }, 200);
         return () => clearInterval(timer);
      }
   }, [routePaths]);

   // 지도 초기화 + 경로선 그리기 (1회)
   useEffect(() => {
      if (!jsReady || !useJsMap || mode !== 'map') return;
      if (mapInitRef.current) return;
      const el = participantMapRef.current;
      if (!el) return;

      mapInitRef.current = true;
      const placeLat = parseFloat(lat);
      const placeLng = parseFloat(lng);
      const center = { lat: placeLat, lng: placeLng };

      const map = new window.google.maps.Map(el, { center, zoom: 13, language: 'ko', region: 'KR' });
      mapInstanceRef.current = map;

      // 약속 장소 마커
      new window.google.maps.Marker({ position: center, map, title: address, animation: window.google.maps.Animation.DROP });

      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(center);
      boundsRef.current = bounds;

      // ★ participantLocations 원본 인덱스 기준으로 처리 (범례 인덱스와 일치시키기 위해)
      const allMembers = participantLocations; // membersRef.current 아닌 전체
      const hasAnyCoords = allMembers.some(p => p.lat && p.lng);
      if (!hasAnyCoords) return;

      const loading = {};
      allMembers.forEach((p, i) => { if (p.lat && p.lng && !p.isRejected) loading[i] = true; });
      setRouteLoading(loading);

      allMembers.forEach((p, i) => {
         if (!p.lat || !p.lng) return; // 좌표 없으면 스킵
         // [5단계] 불참 멤버: 회색 처리
         const color = p.isRejected ? '#9CA3AF' : PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length];
         const origin = { lat: parseFloat(p.lat), lng: parseFloat(p.lng) };
         bounds.extend(origin);

         // 참여자 시작 마커
         new window.google.maps.Marker({
            position: origin, map,
            title: `${p.name}${p.isRejected ? ' (불참)' : p.isSelf ? ' (나)' : ''}`,
            label: { text: p.isRejected ? '✕' : (PARTICIPANT_LABELS[i] || String(i + 1)), color: '#fff', fontWeight: 'bold', fontSize: '11px' },
            icon: {
               path: window.google.maps.SymbolPath.CIRCLE,
               scale: p.isSelf ? 16 : 13,
               fillColor: color, fillOpacity: 1,
               strokeColor: '#fff', strokeWeight: p.isSelf ? 3 : 2,
            },
         });

         // [5단계] 불참 멤버: 경로 계산 스킵
         if (p.isRejected) return;

         // 경로 요청 → Polyline (인덱스 i = participantLocations 인덱스)
         const ds = new window.google.maps.DirectionsService();
         const drawPolyline = (result) => {
            const path = [];
            result.routes[0]?.legs[0]?.steps?.forEach(step => {
               if (step.path) step.path.forEach(pt => path.push(pt));
            });
            const poly = new window.google.maps.Polyline({
               path, map,
               strokeColor: color, strokeWeight: 4, strokeOpacity: 0.85, zIndex: 1,
            });
            polylinesRef.current[i] = poly;
            const dur = result.routes[0]?.legs[0]?.duration?.text;
            setRouteDurations(prev => ({ ...prev, [i]: dur }));
            setRouteLoading(prev => ({ ...prev, [i]: false }));
         };

         // DirectionsService 실패(도메인 제한/REQUEST_DENIED 등) 시 서버 HTTP 프록시로 폴백
         let fallbackCalled = false;
         const httpFallbackSingle = async () => {
            if (fallbackCalled) return;
            fallbackCalled = true;
            // HTTP effect(calcAll)가 이미 모든 경로를 처리 중이면 중복 fetch 방지
            if (httpFallbackTriggered.current) return;
            // 첫 번째 실패 멤버가 jsLoadFailed=true 전환 → JS 지도 회색 div 숨기고 Leaflet 표시
            httpFallbackTriggered.current = true;
            setJsLoadFailed(true);
            let token = null;
            try { token = await auth.currentUser?.getIdToken(); } catch (_) {}
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            const destStr = lat && lng ? `${parseFloat(lat)},${parseFloat(lng)}` : address;
            const originStr = `${parseFloat(p.lat)},${parseFloat(p.lng)}`;
            for (const tmode of ['transit', 'driving', 'walking']) {
               try {
                  const res = await fetch(
                     `${API_BASE_URL}/api/engine/directions?origin=${encodeURIComponent(originStr)}&dest=${encodeURIComponent(destStr)}&mode=${tmode}`,
                     { headers }
                  );
                  const data = await res.json();
                  if (data.status === 'OK' && data.routes?.length > 0) {
                     const leg = data.routes[0].legs[0];
                     setRouteDurations(prev => ({ ...prev, [i]: leg.duration?.text }));
                     const encoded = data.routes[0].overview_polyline?.points;
                     if (encoded) setRoutePaths(prev => ({ ...prev, [i]: decodePolyline(encoded) }));
                     break;
                  }
               } catch (_) {}
            }
            setRouteLoading(prev => ({ ...prev, [i]: false }));
         };

         // 10초 안에 DirectionsService 응답 없으면 HTTP 프록시로 강제 폴백
         const routeTimer = setTimeout(httpFallbackSingle, 10000);

         ds.route({ origin, destination: center, travelMode: window.google.maps.TravelMode.TRANSIT, region: 'KR', language: 'ko' },
            (result, status) => {
               clearTimeout(routeTimer);
               if (status === 'OK') { drawPolyline(result); }
               else {
                  ds.route({ origin, destination: center, travelMode: window.google.maps.TravelMode.DRIVING, region: 'KR' },
                     (r2, s2) => { if (s2 === 'OK') drawPolyline(r2); else httpFallbackSingle(); }
                  );
               }
            }
         );
      });

      map.fitBounds(bounds);
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [jsReady, useJsMap, mode, lat, lng]);

   // 포커스 변경 → 경로선 강조/흐리기 + 지도 pan
   useEffect(() => {
      const polylines = polylinesRef.current;
      polylines.forEach((poly, i) => {
         if (!poly) return;
         if (focusedIdx === null) {
            poly.setOptions({ strokeWeight: 4, strokeOpacity: 0.85, zIndex: 1 });
         } else {
            poly.setOptions({
               strokeWeight: i === focusedIdx ? 7 : 2,
               strokeOpacity: i === focusedIdx ? 1.0 : 0.18,
               zIndex: i === focusedIdx ? 10 : 1,
            });
         }
      });

      if (focusedIdx !== null) {
         const p = membersRef.current[focusedIdx];
         if (p && mapInstanceRef.current) {
            mapInstanceRef.current.panTo({ lat: parseFloat(p.lat), lng: parseFloat(p.lng) });
            mapInstanceRef.current.setZoom(14);
         }
      } else if (mapInstanceRef.current && boundsRef.current) {
         mapInstanceRef.current.fitBounds(boundsRef.current);
      }
   }, [focusedIdx]);

   // 길찾기 탭: 현재 위치 요청
   useEffect(() => {
      if (!address || mode !== 'directions' || currentPos) return;
      if (!navigator.geolocation) { setGeoError('이 기기에서 위치 서비스를 지원하지 않습니다.'); return; }
      setGeoLoading(true);
      navigator.geolocation.getCurrentPosition(
         (pos) => { setCurrentPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGeoLoading(false); },
         () => { setGeoError('현재 위치를 가져올 수 없습니다. 위치 권한을 허용해주세요.'); setGeoLoading(false); },
         { timeout: 8000 }
      );
   }, [mode, address, currentPos]);

   if (!address) return null;

   const getMapUrl = () =>
      lat && lng ? `https://www.google.com/maps?q=${lat},${lng}&output=embed`
                 : `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;

   const getDirectionsUrl = () => {
      const dest = lat && lng ? `${lat},${lng}` : encodeURIComponent(address);
      return currentPos ? `https://maps.google.com/maps?saddr=${currentPos.lat},${currentPos.lng}&daddr=${dest}&dirflg=r&output=embed` : null;
   };

   const directionsUrl = mode === 'directions' ? getDirectionsUrl() : null;

   return (
      <>
         <div className="modal-overlay" onClick={onClose}></div>
         <div className="map-modal">
            <div className="modal-header">
               <h3 className="modal-title">📍 {address}</h3>
               <button className="modal-close-btn" onClick={onClose}><X size={24} /></button>
            </div>

            {/* 탭 전환 */}
            <div style={{ display: 'flex', gap: '8px', padding: '0 16px 12px' }}>
               {[{ id: 'map', icon: MapPin, label: '장소 보기' }, { id: 'directions', icon: Navigation, label: '내 길찾기' }].map(tab => (
                  <button key={tab.id} onClick={() => setMode(tab.id)} style={{
                     flex: 1, padding: '8px', borderRadius: '10px', fontSize: '13px', fontWeight: 600,
                     border: 'none', cursor: 'pointer',
                     background: mode === tab.id ? '#3B82F6' : '#F3F4F6',
                     color: mode === tab.id ? '#fff' : '#6B7280',
                  }}>
                     <tab.icon size={12} style={{ display: 'inline', marginRight: 4 }} />
                     {tab.label}
                  </button>
               ))}
            </div>

            <div className="map-content">
               <div className="map-container">
                  {/* Maps JS 로드 실패 시 Leaflet 지도(조원 마커+경로선) + 범례 */}
                  {jsLoadFailed && mode === 'map' && (
                     <div>
                        {lat && lng
                           /* 좌표 있으면 Leaflet 인터랙티브 지도 */
                           ? <div style={{ width: '100%', height: 340, borderRadius: '12px', overflow: 'hidden', background: '#e5e7eb' }}>
                               <div ref={leafletDivRef} style={{ width: '100%', height: '100%' }} />
                             </div>
                           /* 좌표 없으면 단순 iframe */
                           : <iframe title="location-map" src={getMapUrl()} width="100%" height="340"
                               style={{ border: 0, borderRadius: '12px' }} allowFullScreen="" loading="lazy"
                               referrerPolicy="no-referrer-when-downgrade" />
                        }
                        {/* 참여자 범례 — 서버 HTTP 경로 결과 표시 */}
                        <div style={{ padding: '10px 0 0' }}>
                           {participantLocations.map((p, i) => {
                              const hasCoords = !!(p.lat && p.lng);
                              // [5단계] 불참 멤버: 회색
                              const color = p.isRejected ? '#9CA3AF' : PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length];
                              const dur = routeDurations[i];
                              const loading = routeLoading[i];
                              const srcBadge = p.isRejected ? null : (
                                 p.locationSource === 'gps'
                                    ? <span style={{ fontSize: 10, color: '#10B981', background: '#D1FAE5', borderRadius: 4, padding: '1px 4px', marginLeft: 4 }}>현재</span>
                                    : p.locationSource === 'address'
                                       ? <span style={{ fontSize: 10, color: '#6B7280', background: '#F3F4F6', borderRadius: 4, padding: '1px 4px', marginLeft: 4 }}>집</span>
                                       : null
                              );
                              return (
                                 <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', marginBottom: 4, background: p.isRejected ? '#F3F4F6' : p.isSelf ? color + '12' : '#F9FAFB', borderRadius: 10, border: p.isRejected ? '1px solid #E5E7EB' : p.isSelf ? `1.5px solid ${color}40` : '1px solid #F3F4F6' }}>
                                    <span style={{ background: hasCoords ? color : '#D1D5DB', color: '#fff', borderRadius: '50%', width: 24, height: 24, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
                                       {p.isRejected ? '✕' : PARTICIPANT_LABELS[i]}
                                    </span>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: p.isRejected ? '#9CA3AF' : '#374151', flex: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                       {p.name}
                                       {!p.isRejected && p.isSelf && <span style={{ fontSize: 10, color, marginLeft: 2 }}>(나)</span>}
                                       {srcBadge}
                                    </span>
                                    {p.isRejected ? (
                                       <span style={{ fontSize: 11, color: '#9CA3AF', background: '#E5E7EB', borderRadius: 4, padding: '2px 6px' }}>불참</span>
                                    ) : hasCoords ? (
                                       loading ? <span style={{ fontSize: 11, color: '#9CA3AF' }}>계산 중...</span>
                                       : dur ? <span style={{ fontSize: 13, fontWeight: 700, color }}>{dur}</span>
                                       : <span style={{ fontSize: 11, color: '#9CA3AF' }}>경로 없음</span>
                                    ) : <span style={{ fontSize: 11, color: '#9CA3AF' }}>위치 없음</span>}
                                 </div>
                              );
                           })}
                           <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', marginTop: 2, background: '#FEF2F2', borderRadius: 10, border: '1px solid #FECACA' }}>
                              <span style={{ fontSize: 16, flexShrink: 0 }}>📍</span>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#EF4444' }}>약속 장소</span>
                           </div>
                        </div>
                     </div>
                  )}
                     {/* JS API 지도 — display 토글로 DOM 유지 (언마운트 시 mapInitRef 무효화 방지) */}
                  {useJsMap && !jsLoadFailed && (
                     <div style={{ display: mode === 'map' ? 'block' : 'none' }}>
                        <div ref={participantMapRef} style={{ width: '100%', height: 340, borderRadius: '12px', background: '#e5e7eb' }} />
                        {/* 참여자 범례 — 클릭하면 해당 경로 포커스 */}
                        <div style={{ padding: '10px 0 0' }}>
                           {participantLocations.map((p, i) => {
                              const hasCoords = !!(p.lat && p.lng);
                              // [5단계] 불참 멤버: 회색
                              const color = p.isRejected ? '#9CA3AF' : PARTICIPANT_COLORS[i % PARTICIPANT_COLORS.length];
                              const isFocused = !p.isRejected && focusedIdx === i;
                              const dur = routeDurations[i];
                              const loading = routeLoading[i];
                              const srcBadge = p.isRejected ? null : (
                                 p.locationSource === 'gps'
                                    ? <span style={{ fontSize: 10, color: '#10B981', background: '#D1FAE5', borderRadius: 4, padding: '1px 4px', marginLeft: 4 }}>현재</span>
                                    : p.locationSource === 'address'
                                       ? <span style={{ fontSize: 10, color: '#6B7280', background: '#F3F4F6', borderRadius: 4, padding: '1px 4px', marginLeft: 4 }}>집</span>
                                       : null
                              );
                              return (
                                 <div
                                    key={i}
                                    onClick={() => !p.isRejected && hasCoords && setFocusedIdx(prev => prev === i ? null : i)}
                                    style={{
                                       display: 'flex', alignItems: 'center', gap: 8,
                                       padding: '7px 8px', marginBottom: 4,
                                       background: p.isRejected ? '#F3F4F6' : isFocused ? color + '22' : p.isSelf ? color + '12' : '#F9FAFB',
                                       borderRadius: 10,
                                       border: p.isRejected ? '1px solid #E5E7EB' : isFocused ? `2px solid ${color}` : p.isSelf ? `1.5px solid ${color}40` : '1px solid #F3F4F6',
                                       cursor: p.isRejected ? 'default' : hasCoords ? 'pointer' : 'default',
                                       transition: 'all 0.15s',
                                    }}
                                 >
                                    <span style={{
                                       background: hasCoords ? color : '#D1D5DB',
                                       color: '#fff', borderRadius: '50%',
                                       width: 24, height: 24, flexShrink: 0,
                                       display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                       fontSize: 11, fontWeight: 700,
                                       boxShadow: isFocused ? `0 0 0 3px ${color}55` : p.isSelf ? `0 0 0 2px ${color}40` : 'none',
                                       transform: isFocused ? 'scale(1.15)' : 'scale(1)',
                                       transition: 'all 0.15s',
                                    }}>
                                       {p.isRejected ? '✕' : PARTICIPANT_LABELS[i]}
                                    </span>
                                    <span style={{ fontSize: 13, fontWeight: 600, color: p.isRejected ? '#9CA3AF' : '#374151', flex: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                       {p.name}
                                       {!p.isRejected && p.isSelf && <span style={{ fontSize: 10, color, marginLeft: 2 }}>(나)</span>}
                                       {srcBadge}
                                    </span>
                                    {p.isRejected ? (
                                       <span style={{ fontSize: 11, color: '#9CA3AF', background: '#E5E7EB', borderRadius: 4, padding: '2px 6px' }}>불참</span>
                                    ) : hasCoords ? (
                                       loading ? (
                                          <span style={{ fontSize: 11, color: '#9CA3AF' }}>계산 중...</span>
                                       ) : dur ? (
                                          <span style={{ fontSize: 13, fontWeight: 700, color }}>{dur}</span>
                                       ) : (
                                          <span style={{ fontSize: 11, color: '#9CA3AF' }}>경로 없음</span>
                                       )
                                    ) : (
                                       <span style={{ fontSize: 11, color: '#9CA3AF' }}>위치 없음</span>
                                    )}
                                 </div>
                              );
                           })}
                           {/* 약속 장소 행 */}
                           <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', marginTop: 2, background: '#FEF2F2', borderRadius: 10, border: '1px solid #FECACA' }}>
                              <span style={{ fontSize: 16, flexShrink: 0 }}>📍</span>
                              <span style={{ fontSize: 13, fontWeight: 600, color: '#EF4444' }}>약속 장소</span>
                           </div>
                           {focusedIdx !== null && (
                              <div style={{ textAlign: 'center', marginTop: 6 }}>
                                 <button onClick={() => setFocusedIdx(null)} style={{ fontSize: 11, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>전체 보기</button>
                              </div>
                           )}
                        </div>
                     </div>
                  )}
                  {/* JS API 없을 때 iframe 폴백 */}
                  {!useJsMap && mode === 'map' && (
                     <iframe title="location-map" src={getMapUrl()} width="100%" height="400"
                        style={{ border: 0, borderRadius: '12px' }} allowFullScreen="" loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade" />
                  )}
                  {/* 내 길찾기 탭 */}
                  {mode === 'directions' && (
                     <>
                        {geoLoading && <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', fontSize: 14 }}>현재 위치 가져오는 중...</div>}
                        {geoError && <div style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#EF4444', fontSize: 13, padding: '0 20px', textAlign: 'center' }}>{geoError}</div>}
                        {directionsUrl && !geoLoading && !geoError && (
                           <iframe title="directions-map" src={directionsUrl} width="100%" height="400"
                              style={{ border: 0, borderRadius: '12px' }} allowFullScreen="" loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade" />
                        )}
                        {!geoLoading && !geoError && !directionsUrl && (
                           <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6B7280', fontSize: 13 }}>현재 위치를 불러오는 중...</div>
                        )}
                     </>
                  )}
               </div>
            </div>
         </div>
      </>
   );
};

/**
 * EventDetailModal - 일정 상세 모달
 */
const EventDetailModal = ({ event, user, onClose, onOpenMap, onDelete, previousLocation, isEditing }) => {
   const [isDeleting, setIsDeleting] = useState(false);
   const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
   const { showToast } = useToast();
   if (!event) return null;

   // 날짜 포맷팅
   const formatDate = (dateStr) => {
      const date = new Date(dateStr);
      const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const weekday = weekdays[date.getDay()];
      return `${year}년 ${month}월 ${day}일 (${weekday})`;
   };

   // 시간 계산
   const calculateDuration = (startTime, endTime) => {
      if (!startTime || !endTime) return '';
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [endHour, endMin] = endTime.split(':').map(Number);
      const durationMin = (endHour * 60 + endMin) - (startHour * 60 + startMin);
      const hours = Math.floor(durationMin / 60);
      const mins = durationMin % 60;
      if (hours > 0 && mins > 0) return `${hours}시간 ${mins}분`;
      if (hours > 0) return `${hours}시간`;
      return `${mins}분`;
   };

   // 시간 표시 포맷 (이동시간 분리)
   const renderTimeSection = () => {
      if (event.hasTravelTime && event.travelStartTime && event.travelEndTime) {
         return (
            <div className="time-split-display">
               <div className="time-total">
                  {event.travelStartTime} ~ {event.endTime} 
                  <span className="duration-text"> ({calculateDuration(event.travelStartTime, event.endTime)})</span>
               </div>
               <div className="time-segments">
                  <div className="time-segment travel">
                     <span className="segment-label">이동</span> 
                     {event.travelStartTime}~{event.travelEndTime}
                  </div>
                  <div className="segment-divider">|</div>
                  <div className="time-segment activity">
                     <span className="segment-label">수업</span>
                     {event.time}~{event.endTime}
                  </div>
               </div>
            </div>
         );
      }
      
      return (
         <div className="modal-value">
            {event.time} ~ {event.endTime}
            {event.time && event.endTime && (
               <span className="duration-text"> ({calculateDuration(event.time, event.endTime)})</span>
            )}
         </div>
      );
   };

   return (
      <>
         {/* 오버레이 */}
         <div className="modal-overlay" onClick={onClose}></div>

         {/* 모달 */}
         <div className="event-detail-modal">
            {/* 헤더 */}
            <div className="modal-header">
               <h3 className="modal-title">일정 상세</h3>
               <button className="modal-close-btn" onClick={onClose}>
                  <X size={24} />
               </button>
            </div>

            {/* 내용 */}
            <div className="modal-content">
               {/* 모임이름 */}
               <div className="modal-section">
                  <div className="modal-label">모임이름</div>
                  <div className="modal-value modal-value-large">{event.title}</div>
                  {event.isCoordinated && (
                     <span className="coordinated-badge">확정된 일정</span>
                  )}
               </div>

               {/* 메모 */}
               {event.note && (
                  <div className="modal-section" style={{ backgroundColor: '#fef3c7', borderRadius: '8px', padding: '8px 12px', border: '1px solid #fcd34d' }}>
                     <div className="modal-label" style={{ color: '#92400e' }}>📝 메모</div>
                     <div className="modal-value" style={{ color: '#92400e', whiteSpace: 'pre-wrap' }}>{event.note}</div>
                  </div>
               )}

               {/* 날짜 */}
               <div className="modal-section">
                  <div className="modal-label">
                     <Calendar size={16} />
                     날짜
                  </div>
                  <div className="modal-value">{formatDate(event.date)}</div>
               </div>

               {/* 시간 */}
               <div className="modal-section">
                  <div className="modal-label">
                     <Clock size={16} />
                     시간
                  </div>
                  {renderTimeSection()}
               </div>

               {/* 장소 및 교통정보 통합 */}
               <div className="modal-section modal-location-transport-section">
                  {/* 장소 */}
                  <div
                     className="modal-location-section"
                     onClick={() => {
                        // 우선순위: 1. 일정의 목적지 주소, 2. 사용자 주소
                        const eventLocation = event.location;
                        const userLocation = user && user.address
                           ? (user.addressDetail ? `${user.address} ${user.addressDetail}` : user.address)
                           : null;

                        const displayLocation = eventLocation || userLocation;

                        if (displayLocation) {
                           onClose?.(); // EventDetailModal 먼저 닫아야 MapModal이 보임
                           onOpenMap(
                              displayLocation,
                              eventLocation ? (event.locationLat || null) : user?.addressLat,
                              eventLocation ? (event.locationLng || null) : user?.addressLng,
                              { roomId: event.roomId || null, date: event.date || null, title: event.title || null }
                           );
                        }
                     }}
                     style={{ cursor: (event.location || (user && user.address)) ? 'pointer' : 'default' }}
                  >
                     <div className="modal-label">
                        <MapPin size={16} />
                        장소
                     </div>
                     <div className="modal-value modal-location-value">
                        {event.location || (user && user.address
                           ? (user.addressDetail ? `${user.address} ${user.addressDetail}` : user.address)
                           : '장소 미정')}
                        {(event.location || (user && user.address)) && <span className="map-hint">📍 지도 보기</span>}
                     </div>
                  </div>

                  {/* 교통정보 (이동시간 포함 일정만) */}
                  {event.hasTravelTime && (
                     <div className="modal-transport-section">
                        <div className="modal-label">
                           <MapPin size={16} />
                           교통정보
                        </div>
                        <div className="modal-transport-info">
                           {/* 교통수단 */}
                           <div className="transport-row">
                              <span className="transport-icon">
                                 {event.transportMode === 'driving' && '🚗'}
                                 {event.transportMode === 'transit' && '🚇'}
                                 {event.transportMode === 'walking' && '🚶'}
                                 {!event.transportMode && '🚗'}
                              </span>
                              <span className="transport-text">
                                 {event.transportMode === 'driving' && '자동차'}
                                 {event.transportMode === 'transit' && '대중교통'}
                                 {event.transportMode === 'walking' && '도보'}
                                 {!event.transportMode && '자동차'}
                              </span>
                           </div>

                           {/* 경로 보기 버튼 */}
                           {user && user.address && event.location && (
                              <button
                                 className="route-button"
                                 onClick={(e) => {
                                    e.stopPropagation();

                                    // 🚀 출발지 결정 로직 개선
                                    // 1. 이전 일정이 있으면 그곳을 출발지로 설정 (previousLocation)
                                    // 2. 없으면 내 집을 출발지로 설정 (user.address)
                                    let startAddr, startLat, startLng;

                                    if (previousLocation) {
                                       startAddr = previousLocation.address;
                                       startLat = previousLocation.lat;
                                       startLng = previousLocation.lng;

                                    } else {
                                       startAddr = user.addressDetail ? `${user.address} ${user.addressDetail}` : user.address;
                                       startLat = user.addressLat;
                                       startLng = user.addressLng;
                                    }

                                    // 도착지 정보
                                    const endAddr = event.location;
                                    const endLat = event.locationLat;
                                    const endLng = event.locationLng;

                                    // 좌표가 있으면 좌표 사용, 없으면 주소 사용
                                    if (startLat && startLng && endLat && endLng) {
                                       // 좌표 기반 카카오맵 길찾기
                                       const kakaoMapUrl = `https://map.kakao.com/link/to/${encodeURIComponent(endAddr)},${endLat},${endLng}/from/${encodeURIComponent(startAddr)},${startLat},${startLng}`;
                                       window.open(kakaoMapUrl, '_blank');
                                    } else {
                                       // 주소 기반 카카오맵 검색 (폴백)
                                       // 출발지도 쿼리에 포함하면 좋지만, 카카오맵 웹 URL 스키마 한계로 도착지 검색만 우선 수행
                                       // (길찾기 파라미터가 복잡함)
                                       const kakaoMapUrl = `https://map.kakao.com/link/search/${encodeURIComponent(endAddr)}`;
                                       window.open(kakaoMapUrl, '_blank');
                                       showToast('정확한 경로를 보려면 주소 등록이 필요합니다.');
                                    }
                                 }}
                              >
                                 🗺️ 경로 보기
                              </button>
                           )}
                        </div>
                     </div>
                  )}
               </div>

               {/* 인원수 */}
               <div className="modal-section">
                  <div className="modal-label">
                     <Users size={16} />
                     인원수
                  </div>
                  <div className="modal-value">👥 {event.participants || 1}명 / {event.totalMembers > 0 ? event.totalMembers : (event.participants || 1)}명</div>
                  {event.participantNames && event.participantNames.length > 0 && (
                     <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                        {event.participantNames.map((name, idx) => (
                           <span key={idx} style={{
                              padding: '2px 8px',
                              backgroundColor: '#DBEAFE',
                              color: '#1D4ED8',
                              fontSize: '12px',
                              borderRadius: '9999px'
                           }}>
                              {name}
                           </span>
                        ))}
                     </div>
                  )}
                  {/* 외부 참여자 (채팅으로 추가된 이름들) - 본인 포함하여 표시 */}
                  {event.externalParticipants && event.externalParticipants.length > 0 && (
                     <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '6px' }}>
                        {/* 본인 이름 먼저 표시 */}
                        {user && (user.firstName || user.name) && (
                           <span style={{
                              padding: '2px 8px',
                              backgroundColor: '#DBEAFE',
                              color: '#1D4ED8',
                              fontSize: '12px',
                              borderRadius: '9999px'
                           }}>
                              {user.firstName || user.name}
                           </span>
                        )}
                        {event.externalParticipants.map((p, idx) => (
                           <span key={idx} style={{
                              padding: '2px 8px',
                              backgroundColor: '#DBEAFE',
                              color: '#1D4ED8',
                              fontSize: '12px',
                              borderRadius: '9999px'
                           }}>
                              {p.name}
                           </span>
                        ))}
                     </div>
                  )}
               </div>

               {/* 조율방 정보 (확정된 일정일 경우) */}
               {event.isCoordinated && event.roomName && (
                  <div className="modal-section modal-coordinated-info">
                     <div className="modal-label">조율방</div>
                     <div className="modal-value">📅 {event.roomName}</div>
                  </div>
               )}
                           {/* 삭제 버튼 */}
               {onDelete && (
                  <div className="modal-delete-section">
                     <button
                        className="event-delete-btn"
                        onClick={() => {
                           if (isDeleting) return;
                           setConfirmModal({
                              isOpen: true,
                              title: '일정 삭제',
                              message: '이 일정을 삭제하시겠습니까?',
                              onConfirm: () => {
                                 setIsDeleting(true);
                                 onDelete(event).finally(() => setIsDeleting(false));
                              }
                           });
                        }}
                        disabled={isDeleting}
                     >
                        <Trash2 size={16} />
                        {isDeleting ? '삭제 중...' : '이 일정 삭제'}
                     </button>
                  </div>
               )}
            </div>
         </div>
         <CustomAlertModal
            isOpen={confirmModal.isOpen}
            onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            onConfirm={confirmModal.onConfirm}
            title={confirmModal.title}
            message={confirmModal.message}
            type="warning"
            showCancel={true}
            confirmText="확인"
            cancelText="취소"
         />
      </>
   );
};

export default EventDetailModal;
